<template>
  <div class="d-flex align-center" :class="`${color} ${areDifferentDates || areDateRangeButtonsShown ? 'mr-2' : ''}`" style="border-radius: 20px">
    <v-btn v-show="!(areDifferentDates || areDateRangeButtonsShown)" class="mx-2" :color="color" small @click="selectPrevious" icon>
      <v-icon color="white">mdi-chevron-left</v-icon>
    </v-btn>
    <!-- List of dates for the user to choose -->
    <v-menu :left="!!smAndDown" :close-on-content-click="true" transition="scale-transition" offset-y min-width="auto" max-width="290">
      <template v-slot:activator="{ on, attrs }">
        <v-btn :elevation="0" v-bind="attrs" v-on="on" :small="smAndDown">
          <v-icon :class="mdAndUp ? 'mr-2' : ''">
            {{ areDateRangeButtonsShown ? 'mdi-calendar-range' : 'mdi-' + calendarIcon }}
          </v-icon>
          <p v-if="mdAndUp" class="ma-0">{{ areDateRangeButtonsShown ? $t('custom') : menuButtonLabel }}</p>
        </v-btn>
      </template>
      <v-list>
        <v-list-item
          v-for="predefinedDate in predefinedDates"
          :key="'list-item-' + predefinedDate.label"
          @click="onPredefinedDateSelected(predefinedDate.label, predefinedDate.icon, predefinedDate.date().from, predefinedDate.date().to)"
        >
          <v-list-item-title>{{ $t(predefinedDate.label) }}</v-list-item-title>
        </v-list-item>
        <div v-if="isProd">
          <!-- Extended List of dates for debugging purposes -->
          <v-list-item
            v-for="debugPredefinedDate in debugPredefinedDates"
            :key="'list-item-' + debugPredefinedDate.label"
            @click="
              onPredefinedDateSelected(debugPredefinedDate.label, debugPredefinedDate.icon, debugPredefinedDate.date().from, debugPredefinedDate.date().to)
            "
          >
            <v-list-item-title> {{ $t(debugPredefinedDate.label) }} </v-list-item-title>
          </v-list-item>
        </div>
        <v-list-item @click="customDateRange">
          <v-list-item-title>{{ $t('custom') }}</v-list-item-title>
        </v-list-item>
      </v-list>
    </v-menu>
    <v-btn v-if="!(areDifferentDates || areDateRangeButtonsShown)" class="mx-2" small @click="selectNext" icon>
      <v-icon color="white">mdi-chevron-right</v-icon>
    </v-btn>

    <!-- The two buttons will appear when the user wants to set a custom date range -->
    <v-menu :left="!!smAndDown" :close-on-content-click="false" transition="scale-transition" offset-y min-width="auto" z-index="999">
      <template v-slot:activator="{ on, attrs }">
        <div v-show="areDateRangeButtonsShown">
          <v-btn v-if="smAndDown" class="ml-2 mr-2" elevation="0" small v-bind="attrs" v-on="on">
            <v-icon>mdi-calendar-start</v-icon>
          </v-btn>
          <v-btn v-else class="ml-5 mr-2" elevation="0" v-bind="attrs" v-on="on">{{ rangeFromButtonLabel }}</v-btn>
        </div>
      </template>
      <v-date-picker no-title first-day-of-week="1" v-model="datePickerModel.from" :max="dateRangeSelection.to"></v-date-picker>
    </v-menu>
    <v-menu :left="!!smAndDown" :close-on-content-click="false" transition="scale-transition" offset-y min-width="auto" z-index="999">
      <template v-slot:activator="{ on, attrs }">
        <div v-show="areDateRangeButtonsShown">
          <v-btn v-if="smAndDown" elevation="0" small v-bind="attrs" v-on="on">
            <v-icon>mdi-calendar-end</v-icon>
          </v-btn>
          <v-btn v-else class="mr-10" elevation="0" v-bind="attrs" v-on="on">{{ rangeToButtonLabel }}</v-btn>
        </div>
      </template>
      <v-date-picker no-title first-day-of-week="1" v-model="datePickerModel.to" :min="dateRangeSelection.from"></v-date-picker>
    </v-menu>
  </div>
</template>

<script>
import dateUtils from '@/common/dateUtils';
import { DAY_IN_MS } from '@/common/constants';
import common from '@/mixins/common';

export default {
  name: 'TelemetrieDatePicker',
  emits: ['input'],
  mixins: [common],
  props: {
    dateFrom: {
      type: Date,
      default: () => new Date(),
    },
    dateTo: {
      type: Date,
      default: () => new Date(),
    },
    color: String,
  },
  data: () => ({
    isProd: () => {
      const STAGE = this.$store.state.configs.stage;
      return !['dev', 'alpha', 'test'].includes(STAGE);
    },
    areDateRangeButtonsShown: false,
    calendarIcon: null,
    // YYYY-mm-dd
    predefinedDates: {
      today: {
        label: 'today',
        icon: 'calendar-today',
        date: () => {
          return { from: new Date() };
        },
      },
      lastSevenDays: {
        label: 'last_seven_days',
        icon: 'calendar-week',
        date: () => {
          const today = new Date();
          return { from: new Date(today.getTime() - DAY_IN_MS * 7) };
        },
      },
      lastThirtyDays: {
        label: 'last_thirty_days',
        icon: 'calendar-month',
        date: () => {
          const today = new Date();
          return { from: new Date(today.getTime() - DAY_IN_MS * 30) };
        },
      },
      currentYear: {
        label: 'current_year',
        icon: 'calendar-collapse-horizontal',
        date: () => {
          const currentYear = new Date().getFullYear();
          const firstDay = new Date(currentYear, 0, 1);
          const lastDay = new Date(currentYear, 11, 31);
          return {
            from: firstDay,
            to: lastDay,
          };
        },
      },
    },
    debugPredefinedDates: {
      allDates: {
        label: 'all_dates',
        icon: 'calendar-expand-horizontal',
        date: () => {
          const startDate = new Date(2010, 0, 1);
          return {
            from: startDate,
            to: new Date(),
          };
        },
      },
    },
    datePickerModel: {
      from: null,
      to: null,
    },
    dateRangeSelection: {
      from: dateUtils.reformatDateIsoStringAsYYYYMMDD(new Date()),
      to: dateUtils.reformatDateIsoStringAsYYYYMMDD(new Date()),
    },
  }),
  mounted() {
    this.dateRangeSelection.from = this.$route.params.from;
    this.dateRangeSelection.to = this.$route.params.to;
    this.datePickerModel.from = this.$route.params.from;
    this.datePickerModel.to = this.$route.params.to;
    if (this.dateRangeSelection.from !== this.dateRangeSelection.to) {
      this.calendarIcon = 'calendar-range';
    } else {
      this.calendarIcon = this.predefinedDates.today.icon;
    }
  },
  computed: {
    /**
     * @returns {String|string}
     */
    menuButtonLabel() {
      if (this.areRouteAndSelectionDatesDifferent) {
        return this.formatRouteDates();
      }
      return this.formatSelectionDates();
    },
    /**
     * @returns {String}
     */
    rangeFromButtonLabel() {
      const parsedFromDate = dateUtils.parseYYYYMMDD(this.dateRangeSelection.from);
      const parsedToDate = dateUtils.parseYYYYMMDD(this.dateRangeSelection.to);

      if (parsedFromDate && parsedToDate && parsedFromDate.getFullYear() !== parsedToDate.getFullYear()) {
        return dateUtils.formatAsLocaleNamedShortDateWithYear(parsedFromDate);
      }
      return dateUtils.formatAsLocaleNamedShortDate(parsedFromDate);
    },

    /**
     * @returns {String}
     */
    rangeToButtonLabel() {
      const parsedFromDate = dateUtils.parseYYYYMMDD(this.dateRangeSelection.from);
      const parsedToDate = dateUtils.parseYYYYMMDD(this.dateRangeSelection.to);

      if (parsedFromDate && parsedToDate && parsedFromDate.getFullYear() !== parsedToDate.getFullYear()) {
        return dateUtils.formatAsLocaleNamedShortDateWithYear(parsedToDate);
      }
      return dateUtils.formatAsLocaleNamedShortDate(parsedToDate);
    },
    /**
     * @returns {boolean}
     */
    areDifferentDates() {
      return this.dateRangeSelection.from !== this.dateRangeSelection.to;
    },
  },
  methods: {
    selectPrevious() {
      let date = dateUtils.parseYYYYMMDD(this.dateRangeSelection.from);
      let previousDay = new Date(date.getTime() - DAY_IN_MS);
      this.handleDateChange(previousDay);
    },
    selectNext() {
      let date = dateUtils.parseYYYYMMDD(this.dateRangeSelection.to);
      let nextDay = new Date(date.getTime() + DAY_IN_MS);
      this.handleDateChange(nextDay);
    },
    /**
     * @param {string} label
     * @param {string} icon
     * @param {Date} fromDate
     * @param {Date} toDate
     */
    onPredefinedDateSelected(label, icon, fromDate, toDate = new Date()) {
      this.areDateRangeButtonsShown = false;
      let from;
      let to;
      if (label === this.predefinedDates.today.label) {
        this.dateRangeSelection.from = fromDate;
        this.dateRangeSelection.to = fromDate;
        from = fromDate;
        to = fromDate;
      } else {
        this.dateRangeSelection.from = fromDate;
        this.dateRangeSelection.to = toDate;
        from = fromDate;
        to = toDate;
      }
      this.calendarIcon = icon;
      this.handleDateChange(from, to);
    },
    /**
     * @param {Date} from
     * @param {Date} to
     */
    handleDateChange(from, to = from) {
      this.dateRangeSelection.from = dateUtils.formatDateAsYYYYMMDD(from);
      this.dateRangeSelection.to = dateUtils.formatDateAsYYYYMMDD(to);
      //this.datePickerModel.from = this.dateRangeSelection.from;
      //this.datePickerModel.to = this.dateRangeSelection.to;
      this.$emit('input', { from, to });
    },
    customDateRange() {
      this.areDateRangeButtonsShown = true;
    },
    areRouteAndSelectionDatesDifferent() {
      const routeDateFrom = this.$route.params.from;
      const routeDateTo = this.$route.params.to;
      return !(this.dateRangeSelection.from === routeDateFrom && this.dateRangeSelection.to === routeDateTo);
    },
    formatRouteDates() {
      const routeDateFrom = this.$route.params.from;
      const routeDateTo = this.$route.params.to;
      const areRouteFromToDatesEqual = routeDateFrom === routeDateTo;
      const parsedRouteFromDate = dateUtils.parseYYYYMMDD(routeDateFrom);
      const parsedRouteToDate = dateUtils.parseYYYYMMDD(routeDateTo);
      let dateRouteFromFormatted = dateUtils.formatAsLocaleNamedShortDate(parsedRouteFromDate);
      let dateRouteToFormatted = dateUtils.formatAsLocaleNamedShortDate(parsedRouteToDate);

      if (areRouteFromToDatesEqual) {
        return dateRouteFromFormatted;
      }

      if (parsedRouteFromDate.getFullYear() !== parsedRouteToDate.getFullYear()) {
        dateRouteFromFormatted = dateUtils.formatAsLocaleNamedShortDateWithYear(parsedRouteFromDate);
        dateRouteToFormatted = dateUtils.formatAsLocaleNamedShortDateWithYear(parsedRouteToDate);
      }
      return `${dateRouteFromFormatted} - ${dateRouteToFormatted}`;
    },
    formatSelectionDates() {
      const areFromToDatesEqual = !this.dateRangeSelection.to || this.dateRangeSelection.from === this.dateRangeSelection.to;
      const parsedFromDate = dateUtils.parseYYYYMMDD(this.dateRangeSelection.from);

      if (areFromToDatesEqual) {
        return dateUtils.formatAsLocaleNamedShortDate(parsedFromDate);
      }

      const parsedToDate = dateUtils.parseYYYYMMDD(this.dateRangeSelection.to);
      const dateFromFormatted = this.formatDateWithYearCheck(parsedFromDate, parsedToDate);
      const dateToFormatted = this.formatDateWithYearCheck(parsedToDate, parsedFromDate);
      return `${dateFromFormatted} - ${dateToFormatted}`;
    },
    formatDateWithYearCheck(date1, date2) {
      if (date1.getFullYear() !== date2.getFullYear()) {
        return dateUtils.formatAsLocaleNamedShortDateWithYear(date1);
      }
      return dateUtils.formatAsLocaleNamedShortDate(date1);
    },
  },
  watch: {
    /**
     * @param {Date} newValue
     */
    'dateRangeSelection.from'(newValue) {
      if (newValue instanceof Date || (typeof newValue === 'object' && newValue.constructor === Date)) {
        this.dateRangeSelection.from = dateUtils.reformatDateIsoStringAsYYYYMMDD(newValue.toISOString());
        this.datePickerModel.from = this.dateRangeSelection.from;
        //this.$router.replace({ name: 'telemetrie-range-date', params: { from: this.dateRangeSelection.from, to: this.dateRangeSelection.to } });
      }
    },
    /**
     * @param {Date} newValue
     */
    'dateRangeSelection.to'(newValue) {
      if (newValue instanceof Date || (typeof newValue === 'object' && newValue.constructor === Date)) {
        this.dateRangeSelection.to = dateUtils.reformatDateIsoStringAsYYYYMMDD(newValue.toISOString());
        this.datePickerModel.to = this.dateRangeSelection.to;
        //this.$router.replace({ name: 'telemetrie-range-date', params: { from: this.dateRangeSelection.from, to: this.dateRangeSelection.to } });
      }
    },
    /**
     *
     * @param newValue
     */
    'datePickerModel.from'(newValue) {
      this.dateRangeSelection.from = newValue;
      if (this.$route.params.from !== this.dateRangeSelection.from || this.$route.params.to !== this.dateRangeSelection.to) {
        this.$router.replace({
          name: 'telemetrie-range-date',
          params: { from: this.dateRangeSelection.from, to: this.dateRangeSelection.to },
        });
      }
    },
    /**
     *
     * @param newValue
     */
    'datePickerModel.to'(newValue) {
      this.dateRangeSelection.to = newValue;
      if (this.$route.params.from !== this.dateRangeSelection.from || this.$route.params.to !== this.dateRangeSelection.to) {
        this.$router.replace({
          name: 'telemetrie-range-date',
          params: { from: this.dateRangeSelection.from, to: this.dateRangeSelection.to },
        });
      }
    },
  },
};
</script>
